import React from 'react';

import { Badge } from '@/components/ui/catalyst/badge';
import { Checkbox, CheckboxField } from '@/components/ui/catalyst/checkbox';
import { Label } from '@/components/ui/catalyst/fieldset';
import type {
  ConfigFieldOption,
  WorkflowStatus,
} from '@/services/api/generated/firmsService';

interface MultiSelectFieldProps {
  label: string;
  description?: string | null;
  values: string[];
  options: ConfigFieldOption[];
  onChange: (values: string[]) => void;
  disabled?: boolean;
  isLoading?: boolean;
  status?: WorkflowStatus;
}

/**
 * Generic multi-select field renderer.
 * Renders checkboxes from backend-provided options.
 */
export const MultiSelectField: React.FC<MultiSelectFieldProps> = ({
  label,
  description,
  values,
  options,
  onChange,
  disabled = false,
  isLoading = false,
  status,
}) => {
  const handleToggle = (value: string, checked: boolean) => {
    const newValues = checked
      ? [...values, value]
      : values.filter((v) => v !== value);
    onChange(newValues);
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5">
        <div className="text-xs font-medium text-zinc-950 dark:text-white">
          {label}
        </div>
        {status === 'coming_soon' && (
          <Badge color="zinc" className="text-[10px] py-0 px-1">
            Not Live
          </Badge>
        )}
      </div>
      {description && (
        <div className="text-[11px] leading-tight text-zinc-500 dark:text-zinc-400">
          {description}
        </div>
      )}
      <div className="space-y-1 pt-0.5">
        {options.map((option) => (
          <CheckboxField key={option.value}>
            <Checkbox
              checked={values.includes(option.value)}
              onChange={(checked) => handleToggle(option.value, checked)}
              disabled={disabled || isLoading}
              color="blue"
            />
            <Label className="text-xs">{option.label}</Label>
          </CheckboxField>
        ))}
      </div>
    </div>
  );
};
